import { NORMALLY } from '../Intervals/constants';
import { fetch, FetchFn } from '../Isomorphic/fetch';
import globalThis from '../ponyfill/globalThis';

interface ITimeToConnection {
	uri: string;
	error?: Error;
}

const TIMEOUT_ERROR = 'TimeoutError';

const connectToPingUrl = async (uri: string, fetchFn: FetchFn = fetch): Promise<ITimeToConnection> => {
	const pingUrl = uri + '/ping';
	let error: Error | undefined;
	try {
		if (globalThis.AbortSignal?.timeout) {
			/**
			 * Using AbortSignal.timeout is preferred as the fetch is terminated at the timeout.
			 * The fetch() request does not terminate at timeout even if it is used in Promise.race(),
			 * but continues and is terminated by browser at some point in distant future causing a memory leak.
			 */
			await fetchFn(pingUrl, { signal: globalThis.AbortSignal.timeout(NORMALLY) });
		} else {
			await fetchFn(pingUrl, undefined, NORMALLY, TIMEOUT_ERROR);
		}
	} catch (e) {
		error = e;
	}

	return { uri, error };
};

let cachedUrl: string | null = null;
let ongoingSelection: Promise<string> | null = null;

/**
 * Returns url from supplied list of urls which successfully resolves to `/ping` endpoint,
 * i.e. makes connection without error or any exception like timeout exception.
 * Responses with status codes above 400 are considered successful too, they are not network related issues.
 * @param urls
 * @param fetchFn
 */
export async function chooseUrl(urls: string[], fetchFn: FetchFn = fetch): Promise<string> {
	ongoingSelection = ongoingSelection ?? selectUrl(urls, fetchFn);

	const selectedUrl = await ongoingSelection;
	ongoingSelection = null;
	return selectedUrl;
}

async function selectUrl(urls: string[], fetchFn: FetchFn = fetch): Promise<string> {
	if (cachedUrl) {
		return cachedUrl;
	}

	let error: Error | undefined;
	for (let i = 0; i < urls.length; i++) {
		const result = await connectToPingUrl(urls[i], fetchFn);
		if (!result.error) {
			cachedUrl = result.uri;
			return result.uri;
		}
		error = result.error;
	}

	ongoingSelection = null;
	throw error ?? new Error('Cannot determine working url.');
}

export function invalidateUrl() {
	cachedUrl = null;
}

export function getCachedUrl() {
	if (!cachedUrl) {
		throw new Error('No url is cached');
	}
	return cachedUrl;
}
